/// <amd-module name="VSS/Features/SecurityUI/Roles/Components/RoleAssignmentTable" />
import * as React from "react";

import { ObservableValue } from "VSS/Core/Observable";
import { ScreenBreakpoints } from "VSS/Core/Util/Screen";
import { VssComponent } from "VSS/Platform/Layout";

import { Button } from "VSSUI/Button";
import { Dropdown, DropdownExpandableTextField } from "VSSUI/Dropdown";
import { IListBoxItem } from "VSSUI/ListBox";
import { Observer } from "VSSUI/Observer";
import { ITableBreakpoint, ITableColumn, SimpleTableCell, Table, TableCell } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { IItemProvider } from "VSSUI/Utilities/Provider";
import { VssPersona, IIdentityDetailsProvider } from "VSSUI/VssPersona";

import { SecurityRole, RoleAccess } from "VSS/Features/SecurityUI/Contracts/SecurityRoles";
import { RoleAssignmentItemModel, RoleAssignmentsModel } from "VSS/Features/SecurityUI/Models";
import * as Resources from "VSS/Features/SecurityUI/Roles/Resources";

export interface IRoleAssignmentTableProps {
    disabled: boolean;
    model: RoleAssignmentsModel;
    showAvatars?: boolean;
    userId?: string;
}

class RoleDefinitionsProvider implements IItemProvider<IListBoxItem<SecurityRole>> {
    protected roleDefinitions: SecurityRole[];

    constructor(roleDefinitions: SecurityRole[]) {
        this.roleDefinitions = roleDefinitions;
    }

    public get length(): number {
        return this.roleDefinitions.length;
    }

    public get value(): IListBoxItem<SecurityRole>[] {
        return this.roleDefinitions.map(rd => {
            return { data: rd, id: rd.identifier, text: rd.displayName };
        });
    }
}

export class RoleAssignmentTable extends VssComponent<IRoleAssignmentTableProps> {
    private columns: ITableColumn<RoleAssignmentItemModel>[];
    private roleDefinitionsProvider: RoleDefinitionsProvider;

    constructor(props: IRoleAssignmentTableProps) {
        super(props);
        this.roleDefinitionsProvider = new RoleDefinitionsProvider(this.props.model.getRoleDefinitions());
    }

    public render(): JSX.Element {
        return (
            <Table 
                className="security-role-table" 
                columns={this.getColumns()} 
                itemProvider={this.props.model.getRoleAssignments()} 
                tableBreakpoints={this.tableBreakpoints} 
            />
        );
    }

    private getColumns = (): ITableColumn<RoleAssignmentItemModel>[] => {
        if (!this.columns) {
            this.columns = [
                {
                    id: "user",
                    name: Resources.UserColumnText,
                    onSize: this.onColumnSize,
                    renderCell: this.renderUserCell,
                    width: new ObservableValue(-70)
                },
                {
                    id: "role",
                    name: Resources.RoleColumnText,
                    onSize: this.onColumnSize,
                    renderCell: this.renderRoleCell,
                    width: new ObservableValue(150)
                },
                {
                    id: "delete",
                    renderCell: this.renderDeleteCell,
                    width: new ObservableValue(40)
                }
            ];
        }

        return this.columns;
    };

    private tableBreakpoints: ITableBreakpoint[] = [
        {
            breakpoint: ScreenBreakpoints.xsmall,
            columnWidths: [-70, 130, 40],
        },
        {
            breakpoint: ScreenBreakpoints.small,
            columnWidths: [-70, 170, 40],
        },
    ];

    private renderUserCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<RoleAssignmentItemModel>,
        dataItem: RoleAssignmentItemModel
    ): JSX.Element => {
        const identityDetailsProvider: IIdentityDetailsProvider = {
            getDisplayName: () => dataItem.displayName,
            getIdentityImageUrl: size => dataItem.getIdentityImageUrl(this.context.pageContext)
        };

        return (
            <RoleAssignmentTableCell
                columnIndex={columnIndex}
                dataItem={dataItem}
                key={`col-${columnIndex}`}
                rowIndex={rowIndex}
                tableColumn={tableColumn}
            >
                {this.props.showAvatars && <VssPersona className="avatar" size="small" identityDetailsProvider={identityDetailsProvider} />}
                <Tooltip overflowOnly={true} text={identityDetailsProvider.getDisplayName()}>
                    <div className="wrap-text">
                        {identityDetailsProvider.getDisplayName()}
                        <div className="body-s">{Resources.AccessColumnText}: {dataItem.accessDisplayName}</div>
                    </div>
                </Tooltip>
            </RoleAssignmentTableCell>
        );
    };

    private renderRoleCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<RoleAssignmentItemModel>,
        dataItem: RoleAssignmentItemModel
    ): JSX.Element => {
        return (
            <RoleAssignmentTableCell
                columnIndex={columnIndex}
                dataItem={dataItem}
                key={`col-${columnIndex}`}
                rowIndex={rowIndex}
                tableColumn={tableColumn}
            >
                <Dropdown<SecurityRole>
                    items={this.roleDefinitionsProvider}
                    key={`dropdown-${new Date().getTime()}`}
                    onSelect={(event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<SecurityRole>) =>
                        this.props.model.changeRoleAssignment(dataItem, item.data!)
                    }
                    renderExpandable={props => (
                        <DropdownExpandableTextField
                            {...props}
                            className="flex-grow"
                            disabled={this.props.disabled || this.isMyRow(dataItem)}
                            excludeTabStop={true}
                            inputClassName="roles-dropdown-input"
                        />
                    )}
                    selection={this.getRoleDropdownSelection(dataItem)}
                />
            </RoleAssignmentTableCell>
        );
    };

    private getRoleDropdownSelection(roleAssignment: RoleAssignmentItemModel): DropdownSelection {
        const roleIndex = this.props.model.getRoleDefinitions().findIndex(r => r.identifier === roleAssignment.role.identifier);
        const dropdownSelection = new DropdownSelection();
        dropdownSelection.select(roleIndex);

        return dropdownSelection;
    }

    private renderDeleteCell = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<RoleAssignmentItemModel>,
        dataItem: RoleAssignmentItemModel
    ): JSX.Element => {
        const model = this.props.model;
        return (
            <TableCell className="bolt-table-cell-side-action" columnIndex={columnIndex} key={`col-${columnIndex}`} tableColumn={tableColumn}>
                <div className="bolt-list-cell-content flex-column">
                    <Observer deleted={dataItem.isDeleted()} inheritanceOff={model.isInheritanceOff()}>
                        {(props: { deleted: boolean; inheritanceOff: boolean }) => {
                            const alwaysHidden =
                                this.props.disabled || this.isMyRow(dataItem) || props.deleted || dataItem.access === RoleAccess.Inherited;
                            const showOnFocus = !alwaysHidden && !props.deleted;
                            return (
                                <Button
                                    ariaLabel={Resources.DeleteUserText}
                                    className={css(showOnFocus && "bolt-table-cell-content-reveal", alwaysHidden && "invisible")}
                                    excludeTabStop={true}
                                    iconProps={{ iconName: "Delete" }}
                                    onClick={e => {
                                        model.deleteRoleAssignment(dataItem);
                                        e.preventDefault();
                                    }}
                                    subtle={true}
                                />
                            );
                        }}
                    </Observer>
                </div>
            </TableCell>
        );
    };

    private isMyRow(roleAssignment: RoleAssignmentItemModel): boolean {
        const { userId } = this.props;
        return userId !== undefined && userId === roleAssignment.userId;
    }

    private onColumnSize = (event: MouseEvent, index: number, width: number) => {
        (this.getColumns()[index].width as ObservableValue<number>).value = width;
    };
}

interface IRoleAssignmentTableCellProps {
    children?: React.ReactNode;
    columnIndex: number;
    dataItem: RoleAssignmentItemModel;
    rowIndex: number;
    tableColumn: ITableColumn<RoleAssignmentItemModel>;
}

const RoleAssignmentTableCell = (props: IRoleAssignmentTableCellProps) => {
    return (
        <Observer deleted={props.dataItem.isDeleted()} dirty={props.dataItem.isDirty()}>
            {(p: { deleted: boolean; dirty: boolean }) => {
                return (
                    <SimpleTableCell
                        className={css(p.deleted && "cell-deleted", p.dirty && "cell-dirty")}
                        columnIndex={props.columnIndex}
                        tableColumn={props.tableColumn}
                    >
                        {props.children}
                    </SimpleTableCell>
                );
            }}
        </Observer>
    );
};
